import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  Alert,
  Typography,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { ClinicDialog } from '../dialogs/ClinicDialog';
import { CLINIC_DIALOG_SX, POPUP_COLORS } from '../../constants/popupStyles';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Preview as PreviewIcon,
  ContentCopy as ContentCopyIcon,
  DragIndicator as DragIndicatorIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Quiz as QuizIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card } from '@mui/material';
import toast from 'react-hot-toast';
import { useTenant } from '../../context/TenantContext';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { questionnaireService } from '../../services/questionnaireService';
import { clinicService, Clinic } from '../../services/clinicService';
import patientStatusConfigService, { PatientStatusConfig } from '../../services/patientStatusConfigService';
import { LeadStatusConfig, leadStatusConfigService } from '../../services/leadConfigService';
import emailTemplateService, { EmailTemplate } from '../../services/emailTemplateService';
import { SectionEditor } from './SectionEditor';
import { SectionEditDialog } from './SectionEditDialog';
import { QuestionnairePreview } from './QuestionnairePreview';
import { PopupList } from './PopupList';
import { PopupEditor } from './PopupEditor';
import { QuestionnaireDefinitionData, FormSection, QuestionnairePopup } from '../../types/questionnaire.types';
import ManageColumnsModal from '../modals/ManageColumnsModal';
import SaveViewModal from '../modals/SaveViewModal';
import DataTableToolbar from '../table/DataTableToolbar';
import DataTableContainer from '../table/DataTableContainer';
import DataTableBody from '../table/DataTableBody';
import MobileCardGrid from '../table/MobileCardGrid';
import TablePageLayout from '../table/TablePageLayout';
import useTablePage from '../table/hooks/useTablePage';
import { RowContextMenu, ContextMenuOption } from '../ui/RowContextMenu';
import { questionnaireTypeColumns } from '../../config/questionnaireTypeColumns';
import ReferralAmountsEditor from './ReferralAmountsEditor';

interface QuestionnaireTypeData {
  id?: number;
  name: string;
  title: string;
  path: string;
  type?: 'create_patient' | 'collect_info' | 'il_webhook' | 'create_lead';
  language?: 'en' | 'he';
  clinic_id?: number;
  patient_status?: string;
  /** Lead status on submit - the status a create_lead questionnaire gives the
   *  new lead, or the one a create_patient questionnaire moves the lead to. */
  lead_status?: string;
  ai_summary?: boolean;
  definition: QuestionnaireDefinitionData;
}

interface SortableSectionListItemProps {
  section: FormSection;
  index: number;
  onEdit: (section: FormSection, index: number) => void;
  onDelete: () => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

const SortableSectionListItem: React.FC<SortableSectionListItemProps> = ({
  section,
  index,
  onEdit,
  onDelete,
  isExpanded,
  onToggleExpand,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        p: 2,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: 1 }}>
          <IconButton
            {...attributes}
            {...listeners}
            size="small"
            sx={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          >
            <DragIndicatorIcon fontSize="small" />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              {section.title}
            </Typography>
            {section.description && (
              <Typography variant="body2" color="textSecondary">
                {section.description}
              </Typography>
            )}
          </Box>
          <Chip
            label={`${section.fields.length} fields`}
            size="small"
            variant="outlined"
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
          <IconButton
            size="small"
            onClick={onToggleExpand}
            title={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onEdit(section, index)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={onDelete}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      {isExpanded && (
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          {/* SectionEditor will be rendered here by parent */}
        </Box>
      )}
    </Card>
  );
};

const defaultDefinition: QuestionnaireDefinitionData = {
  sections: [],
};

const defaultFormData: QuestionnaireTypeData = {
  name: '',
  title: '',
  path: '',
  type: 'collect_info',
  language: 'en',
  clinic_id: undefined,
  patient_status: undefined,
  ai_summary: false,
  definition: defaultDefinition,
};

export const QuestionnaireBuilderPage: React.FC = () => {
  const { currentTenant } = useTenant();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<QuestionnaireTypeData>(defaultFormData);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [sectionEditDialogOpen, setSectionEditDialogOpen] = useState(false);
  const [editingSectionIndex, setEditingSectionIndex] = useState<number | null>(null);
  const [editingSection, setEditingSection] = useState<FormSection | undefined>();
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [patientStatusConfigs, setPatientStatusConfigs] = useState<PatientStatusConfig[]>([]);
  const [leadStatusConfigs, setLeadStatusConfigs] = useState<LeadStatusConfig[]>([]);
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [saving, setSaving] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [popupEditorOpen, setPopupEditorOpen] = useState(false);
  const [editingPopup, setEditingPopup] = useState<QuestionnairePopup | undefined>();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Table framework
  const getSortValue = useCallback((q: QuestionnaireTypeData, key: string): any => {
    switch (key) {
      case 'title': return q.title.toLowerCase();
      case 'name': return q.name.toLowerCase();
      case 'type': return (q.type || '').toLowerCase();
      case 'language': return (q.language || '').toLowerCase();
      case 'sections': return q.definition?.sections?.length || 0;
      default: return '';
    }
  }, []);

  const getSearchText = useCallback((q: QuestionnaireTypeData): string[] => {
    return [q.title, q.name, q.type || ''];
  }, []);

  const table = useTablePage<QuestionnaireTypeData>({
    entityType: 'questionnaireTypes',
    columns: questionnaireTypeColumns,
    tenantId: currentTenant?.id,
    getSortValue,
    getSearchText,
  });

  const fetchQuestionnaires = useCallback(async () => {
    if (!currentTenant?.id) return;

    try {
      table.setLoading(true);
      table.setError(null);
      const response = await api.get('/questionnaire-types/', {
        params: { tenant_id: currentTenant.id },
      });
      table.setData(response.data);
    } catch (err: any) {
      console.error('Error fetching questionnaires:', err);
      let errorMessage = 'Failed to load questionnaires';
      if (err.response?.data?.detail) {
        errorMessage = typeof err.response.data.detail === 'string'
          ? err.response.data.detail
          : 'Failed to load questionnaires';
      }
      table.setError(errorMessage);
    } finally {
      table.setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenant?.id]);

  useEffect(() => {
    if (currentTenant?.id) {
      fetchQuestionnaires();
    }
  }, [currentTenant?.id, fetchQuestionnaires]);

  // The dropdown data the dialog needs - clinics, patient and lead statuses,
  // email templates.
  //
  // Loaded with the page rather than when the dialog opens. It used to hang off
  // handleOpenDialog behind an `if (currentTenant?.id)`, which meant the
  // handler had to see a resolved tenant at the instant of the click: the
  // tenant arrives from an async getMyTenant(), so anything that opened the
  // dialog against a stale closure left every dropdown permanently empty, with
  // no request in the network tab to explain it. Keyed on the tenant id here,
  // it simply re-runs when the tenant resolves.
  //
  // Each failure is reported. All four used to end in `.catch(() => {})`, so a
  // request that failed produced an empty dropdown and complete silence - the
  // one shape of bug that is impossible to diagnose from the screen.
  useEffect(() => {
    const tenantId = currentTenant?.id;
    if (!tenantId) return;

    clinicService.getClinics(tenantId)
      .then(setClinics)
      .catch((err) => {
        console.error('Failed to load clinics:', err);
        toast.error('Failed to load clinics');
      });

    patientStatusConfigService.getAll(tenantId)
      .then(setPatientStatusConfigs)
      .catch((err) => {
        console.error('Failed to load patient statuses:', err);
        toast.error('Failed to load patient statuses');
      });

    leadStatusConfigService.getAll(tenantId)
      .then(setLeadStatusConfigs)
      .catch((err) => {
        console.error('Failed to load lead statuses:', err);
        toast.error('Failed to load lead statuses');
      });

    emailTemplateService.getTemplates(undefined, true)
      .then(setEmailTemplates)
      .catch((err) => {
        console.error('Failed to load email templates:', err);
        toast.error('Failed to load email templates');
      });
  }, [currentTenant?.id]);

  const handleOpenDialog = (questionnaire?: QuestionnaireTypeData) => {
    if (questionnaire) {
      setEditingId(questionnaire.id || null);
      setFormData(questionnaire);
    } else {
      setEditingId(null);
      setFormData(defaultFormData);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
    setFormData(defaultFormData);
  };

  const handleEdit = () => {
    if (table.selectedItem) {
      handleOpenDialog(table.selectedItem);
    }
    table.handleMenuClose();
  };

  const handlePreview = () => {
    if (table.selectedItem) {
      setFormData(table.selectedItem);
      setPreviewOpen(true);
    }
    table.handleMenuClose();
  };

  const cleanPayload = (obj: any, preserveKeys: string[] = ['conditionalLogic', 'action']): any => {
    if (typeof obj !== 'object' || obj === null) return obj;
    if (Array.isArray(obj)) {
      return obj.map((item) => cleanPayload(item, preserveKeys));
    }
    return Object.keys(obj).reduce((acc: any, key: string) => {
      const value = obj[key];
      if (preserveKeys.includes(key)) {
        acc[key] = value === undefined ? null : cleanPayload(value, preserveKeys);
      } else if (value !== undefined && value !== null && value !== '') {
        acc[key] = cleanPayload(value, preserveKeys);
      }
      return acc;
    }, {});
  };

  const getAllFields = (): Array<{ name: string; label: string; type: string; options?: any[] }> => {
    if (!formData.definition || !formData.definition.sections) return [];
    return formData.definition.sections.flatMap(section =>
      section.fields.map(field => ({
        name: field.name,
        label: field.label,
        type: field.type,
        options: field.options
      }))
    );
  };

  const hasPaymentField = (): boolean =>
    (formData.definition?.sections || []).some((section) =>
      section.fields.some((field) => field.type === 'stripe_cc')
    );

  const updatePaymentConfig = (patch: Partial<NonNullable<QuestionnaireDefinitionData['payment_config']>>) => {
    setFormData((prev) => ({
      ...prev,
      definition: {
        ...prev.definition,
        sections: prev.definition?.sections || [],
        payment_config: { ...(prev.definition?.payment_config || {}), ...patch },
      },
    }));
  };

  const handleSave = async () => {
    if (!currentTenant?.id) {
      toast.error('Tenant not loaded');
      return;
    }

    if (!formData.name || !formData.title) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.type === 'create_patient') {
      if (!formData.path) {
        toast.error('Path is required for Create Patient type');
        return;
      }
      if (!formData.clinic_id) {
        toast.error('Clinic is required for Create Patient type');
        return;
      }
    }

    if (formData.type === 'il_webhook' && !formData.path) {
      toast.error('Path is required for IL Webhook type');
      return;
    }

    if (formData.type === 'create_lead' && !formData.path) {
      toast.error('Path is required for Create Lead type');
      return;
    }

    if (!formData.definition || formData.definition.sections.length === 0) {
      toast.error('Please add at least one section');
      return;
    }

    try {
      setSaving(true);

      const finalPath = formData.path || formData.name.toLowerCase().replace(/\s+/g, '-');

      const postPayload = cleanPayload({
        name: formData.name,
        title: formData.title,
        path: finalPath,
        type: formData.type,
        language: formData.language || 'en',
        ...(['create_patient', 'create_lead'].includes(formData.type || '') && formData.clinic_id ? { clinic_id: formData.clinic_id } : {}),
        ...(['create_patient', 'collect_info'].includes(formData.type || '') ? { patient_status: formData.patient_status || null } : {}),
        ...(formData.type === 'create_lead' ? { lead_status: formData.lead_status || null } : {}),
        ai_summary: formData.type === 'il_webhook' ? false : (formData.ai_summary ?? false),
        tenant_id: currentTenant.id,
        definition: formData.definition,
      });

      const putPayload = cleanPayload({
        name: formData.name,
        title: formData.title,
        path: finalPath,
        type: formData.type,
        language: formData.language || 'en',
        ...(['create_patient', 'create_lead'].includes(formData.type || '') && formData.clinic_id ? { clinic_id: formData.clinic_id } : {}),
        ...(['create_patient', 'collect_info'].includes(formData.type || '') ? { patient_status: formData.patient_status || null } : {}),
        ...(formData.type === 'create_lead' ? { lead_status: formData.lead_status || null } : {}),
        ai_summary: formData.type === 'il_webhook' ? false : (formData.ai_summary ?? false),
        definition: formData.definition,
      });

      if (editingId) {
        await api.put(`/questionnaire-types/${editingId}/?tenant_id=${currentTenant.id}`, putPayload);
        toast.success('Questionnaire updated successfully');
      } else {
        await api.post('/questionnaire-types/', postPayload);
        toast.success('Questionnaire created successfully');
      }

      handleCloseDialog();
      fetchQuestionnaires();
    } catch (err: any) {
      console.error('Error saving questionnaire:', err);
      console.error('Error response:', err.response?.data);
      let errorMessage = 'Failed to save questionnaire';

      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          errorMessage = err.response.data.detail.map((d: any) => typeof d === 'string' ? d : d.msg).join(', ');
        } else if (typeof err.response.data.detail === 'string') {
          errorMessage = err.response.data.detail;
        }
      } else if (err.response?.data?.errors) {
        errorMessage = Array.isArray(err.response.data.errors)
          ? err.response.data.errors.map((e: any) => typeof e === 'string' ? e : e.msg).join(', ')
          : 'Validation error';
      }

      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!table.selectedItem?.id) return;
    if (!window.confirm('Are you sure you want to delete this questionnaire?')) {
      table.handleMenuClose();
      return;
    }
    try {
      await api.delete(`/questionnaire-types/${table.selectedItem.id}/`);
      toast.success('Questionnaire deleted successfully');
      fetchQuestionnaires();
    } catch (err: any) {
      console.error('Error deleting questionnaire:', err);
      let errorMessage = 'Failed to delete questionnaire';
      if (err.response?.data?.detail) {
        errorMessage = typeof err.response.data.detail === 'string'
          ? err.response.data.detail
          : 'Error deleting questionnaire';
      }
      toast.error(errorMessage);
    }
    table.handleMenuClose();
  };

  const handleDuplicate = async () => {
    if (!table.selectedItem?.id || !currentTenant?.id) return;
    table.handleMenuClose();
    try {
      await questionnaireService.duplicateQuestionnaireType(table.selectedItem.id, currentTenant.id);
      toast.success('Questionnaire duplicated successfully');
      fetchQuestionnaires();
    } catch (err: any) {
      console.error('Error duplicating questionnaire:', err);
      toast.error(err.response?.data?.detail || 'Failed to duplicate questionnaire');
    }
  };

  // Section handlers
  const handleEditSection = (section: FormSection, index: number) => {
    setEditingSection(section);
    setEditingSectionIndex(index);
    setSectionEditDialogOpen(true);
  };

  const handleSaveSectionEdit = (updatedSection: FormSection) => {
    setSectionEditDialogOpen(false);
    setEditingSection(undefined);
    setEditingSectionIndex(null);

    if (editingSectionIndex !== null) {
      handleUpdateSection(editingSectionIndex, updatedSection);
    } else {
      const currentSections = formData.definition?.sections || [];
      const newSection: FormSection = {
        ...updatedSection,
        id: `section_${Date.now()}`,
        order: currentSections.length + 1,
        fields: [],
      };
      setFormData({
        ...formData,
        definition: {
          ...formData.definition,
          sections: [...currentSections, newSection],
        },
      });
    }
  };

  const handleCloseSectionDialog = () => {
    setSectionEditDialogOpen(false);
    setEditingSection(undefined);
    setEditingSectionIndex(null);
  };

  const handleAddSection = () => {
    setEditingSection(undefined);
    setEditingSectionIndex(null);
    setSectionEditDialogOpen(true);
  };

  const handleUpdateSection = (index: number, updatedSection: FormSection) => {
    const currentSections = formData.definition?.sections || [];
    const newSections = [...currentSections];
    newSections[index] = updatedSection;

    setFormData({
      ...formData,
      definition: {
        ...formData.definition,
        sections: newSections,
      },
    });
  };

  const handleDeleteSection = (index: number) => {
    const currentSections = formData.definition?.sections || [];
    setFormData({
      ...formData,
      definition: {
        ...formData.definition,
        sections: currentSections.filter((_, i) => i !== index),
      },
    });
  };

  const handleToggleSectionExpand = (sectionId: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const handleSectionReorder = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const currentSections = formData.definition?.sections || [];
      const oldIndex = currentSections.findIndex((s) => s.id === active.id);
      const newIndex = currentSections.findIndex((s) => s.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newSections = arrayMove(currentSections, oldIndex, newIndex);
        setFormData({
          ...formData,
          definition: {
            ...formData.definition,
            sections: newSections,
          },
        });
      }
    }
  };

  // Popup handlers
  const handleAddPopup = () => {
    setEditingPopup(undefined);
    setPopupEditorOpen(true);
  };

  const handleEditPopup = (popup: QuestionnairePopup) => {
    setEditingPopup(popup);
    setPopupEditorOpen(true);
  };

  const handleSavePopup = (popup: QuestionnairePopup) => {
    const currentPopups = formData.definition?.popups || [];

    if (editingPopup) {
      // Update existing popup
      const updatedPopups = currentPopups.map(p => p.id === popup.id ? popup : p);
      setFormData({
        ...formData,
        definition: {
          ...formData.definition,
          popups: updatedPopups,
        },
      });
    } else {
      // Add new popup
      setFormData({
        ...formData,
        definition: {
          ...formData.definition,
          popups: [...currentPopups, popup],
        },
      });
    }

    setPopupEditorOpen(false);
    setEditingPopup(undefined);
  };

  const handleDeletePopup = (popupId: string) => {
    const currentPopups = formData.definition?.popups || [];
    setFormData({
      ...formData,
      definition: {
        ...formData.definition,
        popups: currentPopups.filter(p => p.id !== popupId),
      },
    });
  };

  const getTypeLabel = (type?: string) => {
    switch (type) {
      case 'create_patient': return 'Create Patient';
      case 'collect_info': return 'Collect Info';
      case 'il_webhook': return 'IL Webhook';
      case 'create_lead': return 'Create Lead';
      default: return type || 'N/A';
    }
  };

  // Column value renderer
  const getColumnValue = (q: QuestionnaireTypeData, columnKey: string): React.ReactNode => {
    switch (columnKey) {
      case 'title':
        return (
          <Box display="flex" alignItems="center">
            <QuizIcon sx={{ mr: 2, color: 'text.secondary' }} />
            <Typography variant="body2" fontWeight="medium">{q.title}</Typography>
          </Box>
        );
      case 'name':
        return <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{q.name}</Typography>;
      case 'type':
        return <Chip label={getTypeLabel(q.type)} size="small" variant="outlined" />;
      case 'language':
        return <Chip label={q.language === 'he' ? 'Hebrew' : 'English'} size="small" variant="outlined" />;
      case 'sections':
        return q.definition?.sections?.length || 0;
      default: return '';
    }
  };

  // Mobile card renderer
  const renderMobileCard = (q: QuestionnaireTypeData) => (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1} sx={{ pr: 5 }}>
        <Box display="flex" alignItems="center">
          <QuizIcon sx={{ mr: 1, color: 'text.secondary' }} />
          <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
            {q.title}
          </Typography>
        </Box>
        <Chip label={getTypeLabel(q.type)} size="small" variant="outlined" />
      </Box>
      <Box sx={{ pl: 4 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontFamily: 'monospace' }}>
          {q.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {q.definition?.sections?.length || 0} sections
        </Typography>
      </Box>
    </>
  );

  const getContextMenuOptions = (): ContextMenuOption[] => {
    if (!table.selectedItem) return [];
    return [
      { label: 'Preview', icon: <PreviewIcon sx={{ mr: 2 }} />, onClick: handlePreview },
      { label: 'Edit', icon: <EditIcon sx={{ mr: 2 }} />, onClick: handleEdit },
      { label: 'Duplicate', icon: <ContentCopyIcon sx={{ mr: 2 }} />, onClick: handleDuplicate },
      { label: 'Delete', icon: <DeleteIcon sx={{ mr: 2 }} />, onClick: handleDelete, color: 'error' as const },
    ];
  };

  // Permission check
  if (!user?.user_privilege?.questionnaires) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You don't have permission to access questionnaire management. Please contact your administrator.
        </Alert>
      </Box>
    );
  }

  return (
    <TablePageLayout loading={table.loading} error={table.error} errorPrefix="Error loading questionnaires:">
      <DataTableToolbar
        {...table.toolbarProps}
        title="Questionnaire Builder"
        searchPlaceholder="Search questionnaires by title or name..."
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Create Questionnaire
          </Button>
        }
      />

      {isMobile ? (
        <MobileCardGrid
          data={table.sortedData}
          renderCard={renderMobileCard}
          onMenuClick={table.handleRowClick}
          getItemKey={(q) => q.id || 0}
          emptyIcon={<QuizIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />}
          emptyMessage="No questionnaires found. Create one to get started."
        />
      ) : (
        <DataTableContainer {...table.containerProps}>
          <DataTableBody
            data={table.sortedData}
            {...table.tableBodyProps}
            getColumnValue={getColumnValue}
            getRowKey={(q) => q.id || 0}
            onRowClick={table.handleRowClick}
          />
        </DataTableContainer>
      )}

      {/* Context Menu */}
      <RowContextMenu {...table.menuProps} options={getContextMenuOptions()} />

      {/* Builder Dialog */}
      <ClinicDialog
        open={openDialog}
        onClose={handleCloseDialog}
        title={editingId ? 'Edit Questionnaire' : 'Create Questionnaire'}
        maxWidth="lg"
        actions={
          <>
            <Button variant="contained" onClick={handleSave} disabled={saving} sx={CLINIC_DIALOG_SX.primaryButton}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
            <Button onClick={handleCloseDialog} sx={CLINIC_DIALOG_SX.cancelButton}>Cancel</Button>
          </>
        }
      >
          {/* Basic Info */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ color: POPUP_COLORS.titleText, fontWeight: 600 }}>
              Basic Information
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth size="small"
                  label="Name (Internal)"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., mdt-triage"
                  InputLabelProps={{ sx: { color: POPUP_COLORS.titleText } }}
                  InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
                />
                <TextField
                  fullWidth size="small"
                  label="Title (Display)"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., MDT Nurse Clinical Triage"
                  InputLabelProps={{ sx: { color: POPUP_COLORS.titleText } }}
                  InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ color: POPUP_COLORS.titleText }}>Type</InputLabel>
                  <Select
                    value={formData.type || 'collect_info'}
                    label="Type"
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as 'create_patient' | 'collect_info' | 'il_webhook' | 'create_lead' })}
                    sx={CLINIC_DIALOG_SX.select}
                  >
                    <MenuItem value="create_patient">Create Patient</MenuItem>
                    <MenuItem value="collect_info">Collect Info</MenuItem>
                    <MenuItem value="create_lead">Create Lead</MenuItem>
                    <MenuItem value="il_webhook">IL Webhook</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ color: POPUP_COLORS.titleText }}>Language</InputLabel>
                  <Select
                    value={formData.language || 'en'}
                    label="Language"
                    onChange={(e) => setFormData({ ...formData, language: e.target.value as 'en' | 'he' })}
                    sx={CLINIC_DIALOG_SX.select}
                  >
                    <MenuItem value="en">English</MenuItem>
                    <MenuItem value="he">Hebrew</MenuItem>
                  </Select>
                </FormControl>
                {(formData.type === 'create_patient' || formData.type === 'il_webhook' || formData.type === 'create_lead') && (
                  <TextField
                    fullWidth size="small"
                    label="Path"
                    value={formData.path}
                    onChange={(e) => setFormData({ ...formData, path: e.target.value })}
                    placeholder="e.g., /mdt-triage"
                    required
                    InputLabelProps={{ sx: { color: POPUP_COLORS.titleText } }}
                    InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
                  />
                )}
                {(formData.type === 'create_patient' || formData.type === 'create_lead') && (
                  <FormControl fullWidth size="small" required={formData.type === 'create_patient'}>
                    <InputLabel sx={{ color: POPUP_COLORS.titleText }}>Clinic</InputLabel>
                    <Select
                      value={formData.clinic_id || ''}
                      label="Clinic"
                      onChange={(e) => setFormData({ ...formData, clinic_id: e.target.value as number })}
                      sx={CLINIC_DIALOG_SX.select}
                    >
                      {clinics.map((clinic) => (
                        <MenuItem key={clinic.id} value={clinic.id}>{clinic.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {(formData.type === 'create_patient' || formData.type === 'collect_info') && (
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ color: POPUP_COLORS.titleText }}>Patient Status on Submit</InputLabel>
                    <Select
                      value={formData.patient_status || ''}
                      label="Patient Status on Submit"
                      onChange={(e) => setFormData({ ...formData, patient_status: e.target.value as string })}
                      sx={CLINIC_DIALOG_SX.select}
                    >
                      <MenuItem value="">{formData.type === 'create_patient' ? '(use default)' : '(no change)'}</MenuItem>
                      {patientStatusConfigs.map((config) => (
                        <MenuItem key={config.key} value={config.key}>{config.title}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {/* Only create_lead sets a lead status. A create_patient
                    questionnaire moves its lead to PATIENT on the backend, so the
                    field does not apply there. */}
                {formData.type === 'create_lead' && (
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ color: POPUP_COLORS.titleText }}>Lead Status on Submit</InputLabel>
                    <Select
                      value={formData.lead_status || ''}
                      label="Lead Status on Submit"
                      onChange={(e) => setFormData({ ...formData, lead_status: e.target.value as string })}
                      sx={CLINIC_DIALOG_SX.select}
                    >
                      <MenuItem value="">
                        (use default)
                      </MenuItem>
                      {leadStatusConfigs.map((config) => (
                        <MenuItem key={config.key} value={config.key}>{config.title}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {formData.type !== 'il_webhook' && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!formData.ai_summary}
                        onChange={(e) => setFormData({ ...formData, ai_summary: e.target.checked })}
                        sx={{ color: POPUP_COLORS.titleText, '&.Mui-checked': { color: POPUP_COLORS.titleText } }}
                      />
                    }
                    label={<Typography sx={{ color: POPUP_COLORS.titleText }}>AI Summary</Typography>}
                  />
                )}
              </Box>
              {/* Exit Popup */}
              {(formData.definition?.popups || []).length > 0 && (
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!formData.definition?.exitPopupId}
                        onChange={(e) => {
                          const popups = formData.definition?.popups || [];
                          setFormData({
                            ...formData,
                            definition: {
                              ...formData.definition,
                              exitPopupId: e.target.checked ? popups[0]?.id : undefined,
                            },
                          });
                        }}
                        sx={{ color: POPUP_COLORS.titleText, '&.Mui-checked': { color: POPUP_COLORS.titleText } }}
                      />
                    }
                    label={<Typography sx={{ color: POPUP_COLORS.titleText }}>Exit Popup</Typography>}
                  />
                  {!!formData.definition?.exitPopupId && (
                    <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                      <InputLabel sx={{ color: POPUP_COLORS.titleText }}>Select Popup</InputLabel>
                      <Select
                        value={formData.definition?.exitPopupId || ''}
                        label="Select Popup"
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            definition: { ...formData.definition, exitPopupId: e.target.value || undefined },
                          })
                        }
                        sx={CLINIC_DIALOG_SX.select}
                      >
                        {(formData.definition?.popups || []).map((popup) => (
                          <MenuItem key={popup.id} value={popup.id}>{popup.title}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Box>
              )}
            </Box>
          </Box>

          {/* Tabs for Sections and Popups */}
          <Box>
            <Tabs
              value={currentTab}
              onChange={(_, newValue) => setCurrentTab(newValue)}
              sx={{
                borderBottom: `1px solid rgba(54,106,117,0.2)`,
                mb: 3,
                '& .MuiTab-root': { color: POPUP_COLORS.titleText, textTransform: 'none' },
                '& .Mui-selected': { color: POPUP_COLORS.titleText, fontWeight: 600 },
                '& .MuiTabs-indicator': { backgroundColor: POPUP_COLORS.titleText },
              }}
            >
              <Tab label="Sections" />
              <Tab label="Popups" />
              <Tab label="Payments" />
            </Tabs>

            {/* Tab Panel: Sections */}
            {currentTab === 0 && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ color: POPUP_COLORS.titleText }}>Sections</Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={handleAddSection} sx={CLINIC_DIALOG_SX.primaryButton}>
                    Add Section
                  </Button>
                </Box>

                {!formData.definition || formData.definition.sections.length === 0 ? (
                  <Alert severity="warning">Add at least one section to your questionnaire</Alert>
                ) : (
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSectionReorder}>
                    <SortableContext items={formData.definition.sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {formData.definition.sections.map((section, index) => (
                          <Box key={section.id}>
                            <SortableSectionListItem
                              section={section}
                              index={index}
                              onEdit={handleEditSection}
                              onDelete={() => handleDeleteSection(index)}
                              isExpanded={expandedSections.has(section.id)}
                              onToggleExpand={() => handleToggleSectionExpand(section.id)}
                            />
                            {expandedSections.has(section.id) && (
                              <Box sx={{ ml: 2, mt: 2, pl: 2, borderLeft: `2px solid ${POPUP_COLORS.titleText}` }}>
                                <SectionEditor
                                  section={section}
                                  onUpdate={(updated) => handleUpdateSection(index, updated)}
                                  allFields={getAllFields()}
                                  availablePopups={formData.definition?.popups || []}
                                  questionnaireTypeId={editingId}
                                  questionnaireKind={formData.type}
                                  language={formData.language}
                                />
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    </SortableContext>
                  </DndContext>
                )}
              </Box>
            )}

            {/* Tab Panel: Popups */}
            {currentTab === 1 && (
              <Box>
                <PopupList
                  popups={formData.definition?.popups || []}
                  onAdd={handleAddPopup}
                  onEdit={handleEditPopup}
                  onDelete={handleDeletePopup}
                />
              </Box>
            )}

            {/* Tab Panel: Payments */}
            {currentTab === 2 && (
              <Box>
                <Typography variant="h6" sx={{ color: POPUP_COLORS.titleText, mb: 1 }}>Payment Settings</Typography>
                {!hasPaymentField() ? (
                  <Alert severity="info">
                    Add a Credit Card (stripe_cc) field to one of the sections to enable payment for this questionnaire.
                  </Alert>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ color: POPUP_COLORS.titleText, mb: 2 }}>
                      Configure the charge amount and what happens after a successful payment.
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                          fullWidth size="small"
                          type="number"
                          label="Amount (£)"
                          value={
                            formData.definition?.payment_config?.amount != null
                              ? formData.definition.payment_config.amount / 100
                              : ''
                          }
                          onChange={(e) => {
                            const pounds = e.target.value;
                            updatePaymentConfig({
                              amount: pounds === '' ? undefined : Math.round(parseFloat(pounds) * 100),
                            });
                          }}
                          placeholder="e.g., 50.00"
                          inputProps={{ min: 0, step: '0.01' }}
                          helperText="Charge amount in GBP. Leave empty for the default (£50.00)."
                          InputLabelProps={{ sx: { color: POPUP_COLORS.titleText } }}
                          InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
                        />
                        <TextField
                          fullWidth size="small"
                          type="number"
                          label="Schedule meeting offset (days)"
                          value={formData.definition?.payment_config?.scheduleMeetingOffsetDays ?? ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            updatePaymentConfig({
                              scheduleMeetingOffsetDays: val === '' ? undefined : parseInt(val, 10),
                            });
                          }}
                          inputProps={{ min: 0, step: 1 }}
                          helperText="Days ahead to set the patient's scheduled meeting. Empty = default (2). 0 = disabled."
                          InputLabelProps={{ sx: { color: POPUP_COLORS.titleText } }}
                          InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
                        />
                      </Box>
                      <ReferralAmountsEditor
                        value={formData.definition?.payment_config?.amount_by_referral}
                        onChange={(next) => updatePaymentConfig({ amount_by_referral: next })}
                        labelColor={POPUP_COLORS.titleText}
                      />
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                          fullWidth size="small"
                          label="Pay Button Label"
                          value={formData.definition?.payment_config?.buttonLabel || ''}
                          onChange={(e) => updatePaymentConfig({ buttonLabel: e.target.value || undefined })}
                          placeholder="Book Appointment"
                          helperText="The amount is shown before this label, e.g. “£50.00 Book Appointment”."
                          InputLabelProps={{ sx: { color: POPUP_COLORS.titleText } }}
                          InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
                        />
                        <TextField
                          fullWidth size="small"
                          label="Description (optional)"
                          value={formData.definition?.payment_config?.description || ''}
                          onChange={(e) => updatePaymentConfig({ description: e.target.value || undefined })}
                          placeholder="e.g., Initial consultation fee"
                          InputLabelProps={{ sx: { color: POPUP_COLORS.titleText } }}
                          InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel sx={{ color: POPUP_COLORS.titleText }}>Patient Status on Success</InputLabel>
                          <Select
                            value={formData.definition?.payment_config?.successPatientStatus || ''}
                            label="Patient Status on Success"
                            onChange={(e) => updatePaymentConfig({ successPatientStatus: (e.target.value as string) || undefined })}
                            sx={CLINIC_DIALOG_SX.select}
                          >
                            <MenuItem value="">(default behavior)</MenuItem>
                            {patientStatusConfigs.map((config) => (
                              <MenuItem key={config.key} value={config.key}>{config.title}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControl fullWidth size="small">
                          <InputLabel sx={{ color: POPUP_COLORS.titleText }}>Email Template on Success</InputLabel>
                          <Select
                            value={formData.definition?.payment_config?.successEmailTemplateId ?? ''}
                            label="Email Template on Success"
                            onChange={(e) =>
                              updatePaymentConfig({
                                successEmailTemplateId: e.target.value === '' ? undefined : Number(e.target.value),
                              })
                            }
                            sx={CLINIC_DIALOG_SX.select}
                          >
                            <MenuItem value="">(default: payment confirmation)</MenuItem>
                            {emailTemplates.map((template) => (
                              <MenuItem key={template.id} value={template.id}>
                                {template.name}{template.language ? ` (${template.language})` : ''}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
            )}
          </Box>
      </ClinicDialog>

      {/* Section Edit Dialog */}
      <SectionEditDialog
        open={sectionEditDialogOpen}
        section={editingSection}
        questionnaireType={formData.type}
        onSave={handleSaveSectionEdit}
        onCancel={handleCloseSectionDialog}
      />

      {/* Popup Editor Dialog */}
      <PopupEditor
        open={popupEditorOpen}
        popup={editingPopup}
        onSave={handleSavePopup}
        onCancel={() => {
          setPopupEditorOpen(false);
          setEditingPopup(undefined);
        }}
      />

      {/* Preview Dialog */}
      <QuestionnairePreview
        open={previewOpen}
        definition={formData.definition}
        title={formData.title}
        onClose={() => setPreviewOpen(false)}
      />

      <ManageColumnsModal {...table.manageColumnsProps} />
      <SaveViewModal {...table.saveViewModalProps} />
    </TablePageLayout>
  );
};
